/**
 * /patient-support-vietnam
 *
 * Single SEO landing page — Patient Support in Vietnam.
 *
 * Built with the same data-driven section components used elsewhere on the
 * site (intro, card grid, steps, FAQ, editorial, CTA, disclaimer) so we
 * inherit the existing branding, palette and mobile layout for free —
 * no new design work, no new CSS.
 *
 * Safety wording (per copy spec):
 *   • VietClinics is an independent healthcare coordination platform.
 *   • Not a hospital, not a clinic, does not provide medical diagnosis.
 *   • Coordinator language ("helps connect / supports / coordinates / guides").
 *   • No guarantees of results; no fixed prices; no named partner hospitals.
 *   • For life-threatening emergencies, dial 115 in Vietnam (115 disclaimer
 *     baked into the shared CoordinationDisclaimer + footer).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, ShieldCheck, MessageCircle, Users, Building2, Heart,
  CheckCircle2, AlertTriangle,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  UrgentIntro, UrgentCardGrid, UrgentSteps, UrgentFaq, UrgentEditorial,
  UrgentCtaBand,
} from '../components/UrgentCareSections';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';
import { useCatalog } from '../context/CatalogContext';
import { getInfoLandingImage } from '../lib/imageOverrides';

const SITE = 'https://vietclinics.com';
const SLUG = '/patient-support-vietnam';

const HERO_IMAGE =
  'https://images.pexels.com/photos/7088530/pexels-photo-7088530.jpeg?auto=compress&cs=tinysrgb&w=1400';
const HERO_FALLBACK =
  'https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=1400';

const SUPPORT_AREAS = [
  { title: 'Doctor & clinic recommendations',          body: 'We help you understand your options across verified local doctors, clinics and hospitals based on your treatment, location and budget.' },
  { title: 'Medical translator & communication',       body: 'English-speaking support and translator help where required — so symptoms, history, questions and expectations are explained clearly.' },
  { title: 'Online & in-person consultation',          body: 'We help arrange video consultations or in-person appointments at suitable clinics, so you can ask questions before you commit.' },
  { title: 'Comparing treatment options',              body: 'We help you compare procedures, recovery timelines and approaches across different doctors — so you can make an informed choice.' },
  { title: 'Treatment plan & price guidance',          body: 'We help you understand what a written treatment plan includes, what is and is not covered, and what realistic price ranges look like.' },
  { title: 'Travel, hotel & appointment coordination', body: 'Where useful, we coordinate transfers, accommodation and the timing of appointments around your trip.' },
  { title: 'Follow-up & aftercare',                    body: 'We help with post-treatment follow-ups, prescription refills, telehealth check-ins and travel insurance documentation.' },
  { title: 'Urgent medical support (where available)', body: 'For non-emergency urgent care, we coordinate with verified local providers — see the Urgent Medical Care hub for live coverage.' },
];

const CHOOSE_DOCTOR_CHECKLIST = [
  'Doctor license and qualifications',
  'Years of experience and number of similar cases performed',
  'Standards of the clinic or hospital where treatment will take place',
  'Whether the procedure is done at a licensed facility',
  'Before-and-after photos or case portfolios where appropriate',
  'Patient reviews and independent feedback',
  'Communication quality and language support',
  'Safety protocols, risks and aftercare policy',
  'What is included in the quoted price (and what is not)',
  'Written follow-up and revision policy',
];

const QUESTIONS_TO_ASK = [
  'Are you licensed for this treatment?',
  'How many similar cases have you performed?',
  'Where will the treatment take place — at a licensed clinic or hospital?',
  'What are the risks and possible complications?',
  'What is included in the price, and what is not?',
  'How long should I stay in Vietnam for safe treatment and recovery?',
  'What follow-up care is included?',
  'Who do I contact if I have concerns after treatment?',
  'Can I see before-and-after photos or patient reviews?',
  'How is aftercare coordinated once I leave Vietnam?',
];

const SUPPORT_JOURNEY = [
  { title: 'Initial enquiry',         body: 'Send a short message with your treatment interest, city and timing. There is no charge to enquire.' },
  { title: 'Basic case review',       body: 'A coordinator reviews your case and (if useful) shares it with verified doctors for a remote opinion.' },
  { title: 'Doctor or clinic options', body: 'We send you suitable doctor and clinic options with credentials, communication notes and indicative pricing.' },
  { title: 'Online consultation',     body: 'Where appropriate, we arrange a video consultation so you can ask questions before any travel or commitment.' },
  { title: 'Treatment plan & price',  body: 'We help you read the doctor\'s written plan, understand what is included, and confirm realistic timing.' },
  { title: 'Translator support',      body: 'Where the doctor does not work entirely in English, we coordinate translator assistance for clarity.' },
  { title: 'Appointments & travel',   body: 'We coordinate appointments, transfers, accommodation and your in-country timeline if you need that level of support.' },
  { title: 'Follow-up support',       body: 'After treatment, we help with check-ins, prescription guidance, telehealth and insurance documentation.' },
];

const WHO_ITS_FOR = [
  'International patients planning treatment in Vietnam',
  'Expats living in Vietnam who want an English-speaking coordinator',
  'Travelers who need medical help while in the country',
  'Medical tourists comparing options across cities',
  'Patients considering cosmetic or plastic surgery',
  'Patients considering dental treatment',
  'Patients needing urgent (non-emergency) care',
  'Patients arranging executive health checkups',
  'Patients seeking a second opinion before deciding',
];

const CITIES = [
  { name: 'Ho Chi Minh City', region: 'Southern Vietnam' },
  { name: 'Hanoi',            region: 'Northern Vietnam' },
  { name: 'Da Nang',          region: 'Central Coast' },
  { name: 'Hoi An',           region: 'Central Coast' },
  { name: 'Vung Tau',         region: 'Southern Coast' },
  { name: 'Nha Trang',        region: 'South Central Coast' },
  { name: 'Phu Quoc',         region: 'Island' },
];

const FAQ = [
  { q: 'What is VietClinics Patient Support?',                                a: 'VietClinics Patient Support is an independent healthcare coordination service for international patients, expats and travelers in Vietnam. We help you understand your options, connect with verified doctors and clinics, arrange consultations and translator support, and coordinate care before, during and after treatment. VietClinics is not a hospital or clinic and does not provide medical diagnosis.' },
  { q: 'Can VietClinics recommend doctors in Vietnam?',                       a: 'Yes. We share doctor and clinic options from our verified partner network, with credentials and communication notes, so you can make an informed choice. We do not pressure-sell any specific doctor — recommendations are based on your treatment type, location, budget and preferences.' },
  { q: 'Can VietClinics help me find an English-speaking doctor?',            a: 'Yes. English-speaking communication is central to our service. Where the lead doctor does not work fully in English, we coordinate translator support so consultations are clear.' },
  { q: 'Can VietClinics provide translator support?',                          a: 'Yes. We can arrange translator assistance for doctor consultations, hospital visits and treatment planning when needed. Communication quality is one of the things we always check before recommending a doctor.' },
  { q: 'Can VietClinics help me compare clinics and hospitals?',              a: 'Yes. We help you compare doctor credentials, clinic standards, treatment approaches, indicative pricing, and patient experience signals — so you can decide with confidence rather than guesswork.' },
  { q: 'Does VietClinics arrange online consultations?',                       a: 'Yes. Where appropriate, we arrange a video consultation with a suitable doctor so you can ask questions and review the proposed plan before any travel or commitment.' },
  { q: 'Can VietClinics help with cosmetic surgery in Vietnam?',               a: 'Yes — see our Cosmetic Surgery in Vietnam hub. We coordinate with verified surgeons and hospital-affiliated centres, and we help you understand the surgeon\'s qualifications, the surgical environment, recovery timing and follow-up.' },
  { q: 'Can VietClinics help with dental treatment in Vietnam?',               a: 'Yes — see our Dental Treatment in Vietnam hub. We coordinate with verified dental clinics for implants, veneers, crowns and full-mouth restoration, with transparent pricing and English-speaking support.' },
  { q: 'Can VietClinics help travelers with urgent medical care?',             a: 'Yes — for non-emergency urgent care, see our Urgent Medical Care in Vietnam hub. VietClinics is not an ambulance service. For life-threatening emergencies, dial 115 in Vietnam.' },
  { q: 'Is VietClinics a clinic or hospital?',                                 a: 'No. VietClinics is an independent healthcare coordination platform. We do not provide medical diagnosis or treatment ourselves. All medical advice, diagnosis and treatment decisions come from licensed healthcare professionals at our partner clinics and hospitals.' },
];

const RELATED_LINKS = [
  { to: '/doctor-recommendation-vietnam', label: 'Doctor Recommendation in Vietnam', sub: 'Find suitable licensed doctors, clinics & specialists' },
  { to: '/medical-tourism-support-vietnam', label: 'Medical Tourism Support in Vietnam', sub: 'Treatment, travel & patient coordination for international patients' },
  { to: '/care-after-discharge-vietnam',  label: 'Care After Hospital Discharge in Vietnam', sub: 'Hotel room recovery support, nurse visits & follow-up coordination' },
  { to: '/how-to-choose-doctor-vietnam',  label: 'How to Choose a Doctor in Vietnam', sub: 'Comparison criteria, red flags, questions to ask' },
  { to: '/medical-translator-vietnam',    label: 'Medical Translator & Communication Support', sub: 'Appointment translation, question prep & treatment plan explanation' },
  { to: '/urgent-medical-care',           label: 'Urgent Medical Care in Vietnam',   sub: 'Hotel doctor, IV therapy & English-speaking telehealth' },
  { to: '/cosmetic-surgery-in-vietnam',   label: 'Cosmetic Surgery in Vietnam',      sub: 'Korean & US-trained surgeons, JCI-accredited centres' },
  { to: '/dental-treatments-in-vietnam',  label: 'Dental Treatment in Vietnam',      sub: 'Implants, veneers and full-mouth restoration' },
  { to: '/hair-restoration-in-vietnam',   label: 'Hair Transplant in Vietnam',       sub: 'Sapphire FUE & DHI by Korean & Turkish-trained surgeons' },
  { to: '/eye-care-treatments-in-vietnam', label: 'Eye Treatment in Vietnam',        sub: 'LASIK, cataract, glaucoma & retina care' },
  { to: '/treatments',                     label: 'Browse All Treatments',           sub: 'Full treatment catalogue with transparent pricing' },
  { to: '/how-it-works',                   label: 'How It Works',                    sub: 'Our 4-step concierge process explained' },
  { to: '/contact',                        label: 'Contact VietClinics',             sub: 'Talk to a coordinator on WhatsApp or email' },
];

const EDITORIAL = {
  reviewedBy: 'VietClinics Patient Concierge Team',
  role: 'Patient support & treatment coordinators',
  methodology:
    'Doctor and clinic options are sourced from our verified partner network, vetted against an internal checklist covering qualifications, language support, clinical standards, transparent pricing and aftercare. Reviewed quarterly.',
  lastUpdated: '2026-02-15',
};

const PatientSupportVietnamPage = () => {
  const { contactInfo } = useContactInfo();
  const { overrides } = useCatalog();
  const heroImage = getInfoLandingImage(overrides, 'patient-support', HERO_IMAGE);
  const whatsappHref = getWhatsAppHref(
    contactInfo,
    'Hi VietClinics, I would like patient support in Vietnam.'
  );

  const canonical = `${SITE}${SLUG}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        '@id': `${canonical}#service`,
        serviceType: 'International Patient Support & Healthcare Coordination',
        provider: { '@type': 'Organization', name: 'VietClinics' },
        areaServed: [{ '@type': 'Country', name: 'Vietnam' }],
        url: canonical,
        description:
          'Independent healthcare coordination for international patients, expats and travelers in Vietnam — doctor recommendations, medical translator support, clinic and hospital comparisons, online consultations, treatment coordination and aftercare.',
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: 'Patient Support in Vietnam',
        description:
          'VietClinics helps international patients, expats and travelers in Vietnam choose suitable doctors, compare clinics, arrange consultations, access translator support and understand treatment options.',
        inLanguage: 'en',
        dateModified: EDITORIAL.lastUpdated,
        lastReviewed: EDITORIAL.lastUpdated,
        reviewedBy: { '@type': 'Organization', name: EDITORIAL.reviewedBy },
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonical}#faq`,
        mainEntity: FAQ.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Patient Support', item: canonical },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Patient Support in Vietnam | Doctor Recommendations, Translators &amp; Medical Help | VietClinics</title>
        <meta
          name="description"
          content="VietClinics helps international patients, expats, and travelers in Vietnam choose suitable doctors, compare clinics, arrange consultations, access translator support, and understand treatment options before making a decision."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Patient Support in Vietnam | VietClinics" />
        <meta
          property="og:description"
          content="Independent support for international patients, expats and travelers — doctor recommendations, translator help, clinic comparisons and treatment coordination in Vietnam."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={heroImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white" data-testid="patient-support-hero">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, #F0FAF8 0%, #DCEFEC 60%, #ffffff 100%)',
          }}
        />
        <div className="relative container mx-auto px-4 max-w-7xl py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[11px] font-bold text-[#1A8F8A] uppercase tracking-widest mb-4 inline-block bg-white/80 backdrop-blur px-3 py-1.5 rounded-full border border-[#DCEFEC]">
                Patient support · Vietnam
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.05] mb-5">
                Patient Support in Vietnam
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-7">
                Independent support for international patients, expats and
                travelers looking for doctor recommendations, medical translator
                help, clinic comparisons and treatment coordination in Vietnam.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/request-treatment"
                  className="inline-flex items-center justify-center gap-2 bg-[#1FA7A1] hover:bg-[#138F8A] text-white font-bold rounded-full px-6 py-3.5 transition-colors"
                  data-testid="patient-support-cta-request"
                >
                  Request Patient Support
                  <ArrowRight className="h-4 w-4" />
                </Link>
                {whatsappHref && (
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border border-slate-200 hover:border-[#1FA7A1] font-bold rounded-full px-6 py-3.5 transition-colors"
                    data-testid="patient-support-cta-doctor-rec"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Ask for Doctor Recommendations
                  </a>
                )}
              </div>
            </div>
            <div
              className="relative rounded-3xl overflow-hidden bg-slate-100"
              style={{ aspectRatio: '4 / 3' }}
            >
              <img
                src={heroImage}
                onError={(e) => { e.currentTarget.src = HERO_FALLBACK; }}
                alt="VietClinics patient support consultation in Vietnam"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP (compact, no claims) ─────────────────────────────── */}
      <section className="bg-white border-y border-slate-100" data-testid="patient-support-trust">
        <div className="container mx-auto px-4 max-w-7xl py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: ShieldCheck, label: 'Verified partner network' },
              { icon: Users,       label: 'English-speaking coordinators' },
              { icon: Building2,   label: 'Independent — not a hospital' },
              { icon: Heart,       label: 'Support before, during & after' },
            ].map(({ icon: Icon, label }, i) => (
              <div key={label} className="flex items-center gap-3" data-testid={`patient-support-trust-${i + 1}`}>
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: '#DCEFEC' }}
                >
                  <Icon className="h-4 w-4 text-[#1A8F8A]" />
                </div>
                <p className="text-xs font-bold text-slate-800 leading-tight">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INTRODUCTION ─────────────────────────────────────────────────── */}
      <UrgentIntro
        heading="Healthcare in another country can feel confusing — we help make it clearer."
        paragraphs={[
          'Choosing a doctor in a country you do not know well is stressful. You may not be sure who to trust, what to ask, or whether the clinic is the right fit for your treatment. Language, prices, treatment plans, recovery timing and aftercare can all feel hard to compare from abroad.',
          'VietClinics is an independent healthcare coordination platform. We help international patients, expats and travelers understand their options in Vietnam — connecting you with suitable doctors and clinics, arranging consultations, providing translator support where needed, and guiding you through the journey before, during and after treatment.',
          'We are not a clinic or hospital. We do not provide medical diagnosis. All medical advice, diagnosis and treatment decisions come from licensed healthcare professionals at our partner clinics and hospitals — our role is to help you find the right ones, ask the right questions, and feel supported every step of the way.',
        ]}
        testId="patient-support-intro"
      />

      {/* ── WHAT WE HELP WITH ────────────────────────────────────────────── */}
      <UrgentCardGrid
        eyebrow="What VietClinics helps with"
        heading="Practical patient support across your healthcare journey"
        items={SUPPORT_AREAS}
        testId="patient-support-areas"
      />

      {/* ── DOCTOR RECOMMENDATIONS ───────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="patient-support-doctor-recs">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Doctor recommendations in Vietnam
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-6">
            Finding the right doctor in Vietnam — without the guesswork
          </h2>
          <div className="space-y-5 text-base lg:text-lg text-slate-600 leading-relaxed">
            <p>
              VietClinics helps patients find suitable doctors, clinics and hospitals based
              on your treatment type, location, doctor experience, the standards of the
              clinic or hospital, communication quality, international patient experience,
              your budget, and your personal needs.
            </p>
            <p>
              Our partner network includes licensed doctors and English-speaking clinics
              across Vietnam — trusted by international patients, expats and travelers
              who want clear support when choosing where to receive care. We share
              credentials, case experience and indicative pricing up front, so you can
              make an informed choice rather than relying on guesswork.
            </p>
          </div>
        </div>
      </section>

      {/* ── TRANSLATOR SUPPORT ───────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="patient-support-translator">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Medical translator &amp; communication support
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-6">
            Clear communication is part of safe care
          </h2>
          <div className="space-y-5 text-base lg:text-lg text-slate-600 leading-relaxed">
            <p>
              Communication is one of the most important parts of medical treatment.
              Symptoms, medical history, expectations, questions and concerns all
              need to be understood clearly — by both you and your doctor.
            </p>
            <p>
              VietClinics provides English-speaking patient support, and where the lead
              doctor does not work fully in English, we coordinate medical translator
              assistance so consultations stay clear. This is especially helpful for
              expats and foreigners who want to be confident they are being understood —
              and that they understand the treatment plan in their own language.
            </p>
          </div>
        </div>
      </section>

      {/* ── CHOOSING THE RIGHT DOCTOR (CHECKLIST) ─────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="patient-support-checklist">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            How to choose the right doctor in Vietnam
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            What to check before deciding
          </h2>
          <ul className="grid sm:grid-cols-2 gap-3">
            {CHOOSE_DOCTOR_CHECKLIST.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-white border border-slate-100 rounded-2xl p-4"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                data-testid={`patient-support-checklist-${i + 1}`}
              >
                <CheckCircle2 className="h-5 w-5 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 font-semibold leading-snug">{item}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── QUESTIONS TO ASK ─────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="patient-support-questions">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Questions to ask before choosing a doctor
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            Prepare these questions for your consultation
          </h2>
          <div className="space-y-3">
            {QUESTIONS_TO_ASK.map((q, i) => (
              <div
                key={i}
                className="flex items-start gap-3 bg-white border border-slate-100 rounded-xl px-5 py-4"
                style={{ boxShadow: '0 2px 12px rgba(15,42,85,0.04)' }}
                data-testid={`patient-support-question-${i + 1}`}
              >
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white font-extrabold text-xs flex-shrink-0 mt-0.5"
                  style={{ background: '#1FA7A1' }}
                >
                  {i + 1}
                </span>
                <p className="text-sm md:text-base text-slate-700 font-semibold leading-snug">{q}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SUPPORT JOURNEY (steps) ──────────────────────────────────────── */}
      <UrgentSteps
        eyebrow="Support before, during and after treatment"
        heading="The VietClinics patient support journey"
        steps={SUPPORT_JOURNEY}
        testId="patient-support-journey"
      />

      {/* ── WHO IT'S FOR ─────────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="patient-support-who">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Who this service is for
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            We support patients with very different needs
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {WHO_ITS_FOR.map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-3 bg-white border border-slate-100 rounded-2xl p-4"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                data-testid={`patient-support-who-${i + 1}`}
              >
                <Users className="h-5 w-5 text-[#1A8F8A] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 font-semibold leading-snug">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CITIES COVERED ───────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="patient-support-cities">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Cities covered
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            Patient support across Vietnam
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {CITIES.map((c) => (
              <div
                key={c.name}
                className="bg-[#F0FAF8] border border-[#DCEFEC] rounded-2xl px-4 py-3"
                data-testid={`patient-support-city-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <p className="font-extrabold text-slate-900 text-sm">{c.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{c.region}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-5 max-w-2xl">
            Support availability varies by city, doctor and treatment type. We confirm
            what is available for your specific request once you enquire.
          </p>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <UrgentFaq
        heading="Patient support in Vietnam — frequently asked"
        items={FAQ}
        testId="patient-support-faq"
      />

      {/* ── RELATED / INTERNAL LINKS ─────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="patient-support-related">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Related VietClinics support
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            Explore the rest of our patient support
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {RELATED_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="group bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl p-5 transition-colors flex items-start justify-between gap-4"
                data-testid={`patient-support-related-${link.to.replace(/\W+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <div>
                  <p className="font-extrabold text-slate-900 text-sm leading-tight">{link.label}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-snug">{link.sub}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── EDITORIAL / E-E-A-T ─────────────────────────────────────────── */}
      <UrgentEditorial editorial={EDITORIAL} testId="patient-support-editorial" />

      {/* ── INDEPENDENT PLATFORM DISCLAIMER (per copy spec) ─────────────── */}
      <section className="py-10 bg-white" data-testid="patient-support-disclaimer-section">
        <div className="container mx-auto px-4 max-w-4xl">
          <div
            className="bg-amber-50/60 border border-amber-100 rounded-2xl p-5 lg:p-6 flex gap-3"
            data-testid="patient-support-disclaimer"
          >
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm leading-relaxed text-amber-900">
              <p className="font-bold mb-1">Important notice</p>
              <p>
                VietClinics is an independent healthcare coordination platform.
                VietClinics is <strong>not a hospital or clinic</strong> and does
                <strong> not provide medical diagnosis</strong>. All medical advice,
                diagnosis, and treatment decisions must come from licensed healthcare
                professionals. For life-threatening emergencies in Vietnam, dial
                <strong> 115</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
      <UrgentCtaBand
        heading="Need Help Choosing the Right Doctor in Vietnam?"
        subheading="Send us your treatment needs and we will help you understand your options, arrange suitable doctor recommendations, and support your healthcare journey in Vietnam."
        testId="patient-support-final-cta"
      />

      <Footer />
    </div>
  );
};

export default PatientSupportVietnamPage;
